import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LOGIN_URL } from "@/constants/links";

const Wordmark = () => (
  <span className="wm">
    Clear<span style={{ color: "#2FD3C6" }}>view</span>
    <span style={{ color: "#EC4899" }}>.</span>
  </span>
);

const NAV_ITEMS = [
  { to: "/how-it-works", label: "How it works", testid: "nav-how" },
  { to: "/reports", label: "Reports", testid: "nav-reports" },
  { to: "/security", label: "Security", testid: "nav-security" },
];

export const Header = () => {
  const active = ({ isActive }) => (isActive ? "active" : undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const close = useCallback(() => setMenuOpen(false), []);

  // Close on route change
  useEffect(() => { close(); }, [location.pathname, close]);

  // Close on Esc; lock body scroll while open
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen, close]);

  return (
    <header className="site-header" data-testid="site-header">
      <div className="container nav">
        <Link to="/" className="logo" aria-label="Clearview home" data-testid="logo-home-link">
          <Wordmark />
        </Link>

        {/* Desktop nav — unchanged */}
        <nav className="navlinks" aria-label="Primary">
          {NAV_ITEMS.map((it) => (
            <NavLink key={it.to} to={it.to} className={active} data-testid={it.testid}>
              {it.label}
            </NavLink>
          ))}
          <a className="signin" href={LOGIN_URL} data-testid="nav-signin">Sign in</a>
          <Link className="pill sm" to="/apply" data-testid="nav-cta-apply">Apply for a demo</Link>
        </nav>

        {/* Mobile cluster — pill + hamburger. Hidden on desktop via CSS. */}
        <div className="mobile-cluster" aria-hidden={false}>
          <Link className="pill sm mobile-cta" to="/apply" data-testid="nav-cta-apply-mobile">Apply for a demo</Link>
          <button
            type="button"
            className={`hamburger ${menuOpen ? "is-open" : ""}`}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu-panel"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
          </button>
        </div>
      </div>

      {/* Mobile overlay menu */}
      <div
        className={`mobile-menu-overlay ${menuOpen ? "is-open" : ""}`}
        onClick={close}
        aria-hidden={!menuOpen}
        data-testid="mobile-menu-overlay"
      />
      <nav
        id="mobile-menu-panel"
        className={`mobile-menu-panel ${menuOpen ? "is-open" : ""}`}
        aria-label="Mobile primary"
        aria-hidden={!menuOpen}
        data-testid="mobile-menu-panel"
      >
        {NAV_ITEMS.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={active}
            data-testid={`mobile-${it.testid}`}
            onClick={close}
            tabIndex={menuOpen ? 0 : -1}
          >
            {it.label}
          </NavLink>
        ))}
        <a
          className="signin"
          href={LOGIN_URL}
          data-testid="mobile-nav-signin"
          onClick={close}
          tabIndex={menuOpen ? 0 : -1}
        >
          Sign in
        </a>
        <Link
          className="pill sm mobile-menu-cta"
          to="/apply"
          data-testid="mobile-nav-cta-apply"
          onClick={close}
          tabIndex={menuOpen ? 0 : -1}
        >
          Apply for a demo
        </Link>
      </nav>
    </header>
  );
};
